import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Card, Row, Col, Typography, Button, Tag, Space, Statistic,
  Spin, message, Table, Modal, Form, Input, InputNumber,
  DatePicker, Select, Tabs, Progress, Empty, Tooltip, Divider,
  Upload, Image, Badge, Alert, Popconfirm
} from 'antd';
import {
  ShopOutlined,
  PlusOutlined,
  EnvironmentOutlined,
  PhoneOutlined,
  ShoppingCartOutlined,
  FileTextOutlined,
  CalendarOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ClockCircleOutlined,
  SearchOutlined,
  EyeOutlined,
  UploadOutlined,
  SyncOutlined,
  TrophyOutlined,
  TeamOutlined,
  CarOutlined,
  AuditOutlined,
  DeleteOutlined,
  GlobalOutlined,
  ArrowRightOutlined,
  ArrowLeftOutlined,
  KeyOutlined,
  SendOutlined,
  BarChartOutlined,
  GiftOutlined,
  CameraOutlined,
  UserOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend
} from 'recharts';
import api, { API_BASE_URL } from '../api';
import { useNavigate } from 'react-router-dom';
import StaffLayout from './StaffLayout';

const { Title, Text, Paragraph } = Typography;
const { Option } = Select;
const { TextArea } = Input;

const fmtCurrency = (val) =>
  `₹${(Number(val) || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const VISIT_TYPES = ['Follow-up', 'New Visit', 'Complaint', 'Demo'];
const CLIENT_TYPES = ['Retailer', 'Distributor', 'Wholesaler'];
const PAYMENT_METHODS = ['Cash', 'Online', 'Cheque', 'UPI', 'Credit'];

const StaffSales = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');

  // Selected date for Sales Report (matching SalesReportScreen.js)
  const [selectedReportDate, setSelectedReportDate] = useState(dayjs());

  // Summary State (matching SalesScreen.js)
  const [summary, setSummary] = useState({
    totalAmount: 0,
    totalOrders: 0,
    conversionRate: 0,
    avgOrderValue: 0,
    target: 0
  });

  // Weekly Trend Data (matching SalesReportScreen.js)
  const [weeklySalesData, setWeeklySalesData] = useState([]);

  // Target & Incentive State (matching TargetsScreen.js)
  const [targetPeriod, setTargetPeriod] = useState('daily'); // 'daily' | 'weekly' | 'monthly'
  const [targetLoading, setTargetLoading] = useState(false);
  const [targetData, setTargetData] = useState({ targetAmount: 0, targetOrders: 0 });
  const [achievedData, setAchievedData] = useState({ amount: 0, orders: 0 });
  const [incentiveData, setIncentiveData] = useState(null);

  // Visits State (matching VisitHistoryScreen.js)
  const [visits, setVisits] = useState([]);
  const [visitTab, setVisitTab] = useState('ALL'); // 'ALL' | 'VERIFIED' | 'PENDING'
  const [visitSearch, setVisitSearch] = useState('');
  const [selectedVisit, setSelectedVisit] = useState(null);
  const [visitDetailsVisible, setVisitDetailsVisible] = useState(false);

  // Orders State (matching OrderHistoryScreen.js)
  const [orders, setOrders] = useState([]);
  const [orderTab, setOrderTab] = useState('ALL'); // 'ALL' | 'ONLINE' | 'CASH' | 'CREDIT'
  const [orderSearch, setOrderSearch] = useState('');
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [orderDetailsVisible, setOrderDetailsVisible] = useState(false);

  // Master Products & Clients Directory
  const [productsMaster, setProductsMaster] = useState([]);
  const [clientsMaster, setClientsMaster] = useState([]);
  const [assignedJobs, setAssignedJobs] = useState([]);
  const [selectedJob, setSelectedJob] = useState(null);
  const [jobDetailModalVisible, setJobDetailModalVisible] = useState(false);
  const [jobActionLoading, setJobActionLoading] = useState(false);
  const [activeJobId, setActiveJobId] = useState(null);

  // Client Picker Modal (SS1 Pick button)
  const [clientPickerVisible, setClientPickerVisible] = useState(false);
  const [clientPickerTarget, setClientPickerTarget] = useState('visit'); // 'visit' | 'order'
  const [clientPickerSearch, setClientPickerSearch] = useState('');

  // 1. Visit Form Modal (Matching SS1 100%)
  const [visitModalVisible, setVisitModalVisible] = useState(false);
  const [visitSubmitting, setVisitSubmitting] = useState(false);
  const [visitForm] = Form.useForm();
  const [visitGeo, setVisitGeo] = useState({ lat: null, lng: null, address: '' });
  const [visitSignatureFile, setVisitSignatureFile] = useState(null);
  const [sendingOtp, setSendingOtp] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [phoneError, setPhoneError] = useState('');

  // 2. Order Form Modal (Matching OrderFormScreen.js 100%)
  const [orderModalVisible, setOrderModalVisible] = useState(false);
  const [orderSubmitting, setOrderSubmitting] = useState(false);
  const [orderForm] = Form.useForm();
  const [orderItems, setOrderItems] = useState([]);
  const [orderProofFile, setOrderProofFile] = useState(null);
  const [orderGeo, setOrderGeo] = useState({ lat: null, lng: null, address: '' });

  // Custom Product modal inside Order Form
  const [customProductModalVisible, setCustomProductModalVisible] = useState(false);
  const [customProductForm] = Form.useForm();

  // Current logged in user
  const [currentUser, setCurrentUser] = useState(null);
  useEffect(() => {
    try {
      const uStr = sessionStorage.getItem('impersonate_user') || localStorage.getItem('user');
      if (uStr) setCurrentUser(JSON.parse(uStr));
    } catch (_) {}
  }, []);

  // Fetch Geolocation helper
  const captureGeo = () => {
    return new Promise((resolve) => {
      if (!navigator.geolocation) {
        return resolve({ lat: null, lng: null, address: '' });
      }
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          const lat = Number(pos.coords.latitude.toFixed(6));
          const lng = Number(pos.coords.longitude.toFixed(6));
          resolve({
            lat,
            lng,
            address: `Coordinates: ${lat}, ${lng}`
          });
        },
        (err) => {
          console.warn('Geolocation capture error:', err);
          resolve({ lat: null, lng: null, address: '' });
        },
        { enableHighAccuracy: true, timeout: 7000, maximumAge: 60000 }
      );
    });
  };

  // Load Sales Data & Summary
  const loadSalesData = useCallback(async () => {
    try {
      setLoading(true);
      const dateIso = selectedReportDate.format('YYYY-MM-DD');

      const weekStart = selectedReportDate.startOf('week');
      const weekEnd = selectedReportDate.endOf('week');
      const startIso = weekStart.format('YYYY-MM-DD');
      const endIso = weekEnd.format('YYYY-MM-DD');

      const [sumRes, weeklyRes, visitsRes, ordersRes, prodsRes, clientsRes, jobsRes] = await Promise.all([
        api.get(`/sales/summary?date=${dateIso}`).catch(() => ({ data: null })),
        api.get(`/sales/weekly?startDate=${startIso}&endDate=${endIso}`).catch(() => ({ data: null })),
        api.get('/sales/my-visits').catch(() => ({ data: null })),
        api.get('/sales/my-orders').catch(() => ({ data: null })),
        api.get('/sales/order-products').catch(() => ({ data: null })),
        api.get('/sales/my-clients').catch(() => ({ data: null })),
        api.get('/sales/assigned_jobs').catch(() => ({ data: null }))
      ]);

      if (sumRes.data?.success && sumRes.data?.summary) {
        setSummary(sumRes.data.summary);
      }

      if (weeklyRes.data?.success && Array.isArray(weeklyRes.data?.data)) {
        const rawWeekly = weeklyRes.data.data;
        const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
        setWeeklySalesData(rawWeekly.map((val, idx) => ({
          day: dayNames[idx] || `Day ${idx + 1}`,
          sales: Number(val || 0)
        })));
      } else {
        const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
        setWeeklySalesData(dayNames.map(d => ({ day: d, sales: 0 })));
      }

      if (visitsRes.data?.success && Array.isArray(visitsRes.data?.visits)) {
        setVisits(visitsRes.data.visits);
      }

      if (ordersRes.data?.success && Array.isArray(ordersRes.data?.orders)) {
        setOrders(ordersRes.data.orders);
      }

      if (prodsRes.data?.success && Array.isArray(prodsRes.data?.products)) {
        setProductsMaster(prodsRes.data.products);
      }

      if (clientsRes.data?.success && Array.isArray(clientsRes.data?.clients)) {
        setClientsMaster(clientsRes.data.clients);
      }

      if (jobsRes.data?.success && Array.isArray(jobsRes.data?.jobs)) {
        setAssignedJobs(jobsRes.data.jobs);
      }
    } catch (err) {
      console.error('Failed to load sales data:', err);
    } finally {
      setLoading(false);
    }
  }, [selectedReportDate]);

  // Load Target & Incentive (matching TargetsScreen.js)
  const loadTargetAndIncentive = useCallback(async (p) => {
    try {
      setTargetLoading(true);
      const [tgtRes, incRes] = await Promise.all([
        api.get(`/sales/targets/current?period=${p}`).catch(() => ({ data: null })),
        api.get(`/sales/incentives/current?period=${p}`).catch(() => ({ data: null }))
      ]);

      const tgt = tgtRes.data?.target || {};
      setTargetData({
        targetAmount: Number(tgt.targetAmount || 0),
        targetOrders: Number(tgt.targetOrders || 0)
      });

      if (p === 'daily') {
        const todayIso = dayjs().format('YYYY-MM-DD');
        const sRes = await api.get(`/sales/summary?date=${todayIso}`).catch(() => ({ data: null }));
        const sum = sRes.data?.summary || {};
        setAchievedData({
          amount: Number(sum.totalAmount || 0),
          orders: Number(sum.totalOrders || 0)
        });
      } else {
        const now = dayjs();
        const start = p === 'weekly' ? now.startOf('week') : now.startOf('month');
        const filtered = orders.filter((o) => dayjs(o.orderDate).isAfter(start));
        setAchievedData({
          amount: filtered.reduce((sum, o) => sum + Number(o.totalAmount || 0), 0),
          orders: filtered.length
        });
      }

      setIncentiveData(incRes.data?.incentive || null);
    } catch (err) {
      console.error('Load target error:', err);
    } finally {
      setTargetLoading(false);
    }
  }, [orders]);

  useEffect(() => {
    loadSalesData();
  }, [loadSalesData]);

  useEffect(() => {
    loadTargetAndIncentive(targetPeriod);
  }, [targetPeriod, loadTargetAndIncentive]);

  // Target percent calculation (matching TargetsScreen.js)
  const targetPercent = useMemo(() => {
    const amt = Number(achievedData.amount || 0);
    const tgtAmt = Number(targetData.targetAmount || 0);
    if (!tgtAmt) return 0;
    return Math.max(0, Math.min(100, Math.round((amt / tgtAmt) * 100)));
  }, [achievedData, targetData]);

  // Filtered Visits
  const filteredVisits = useMemo(() => {
    return visits.filter((v) => {
      const matchSearch =
        !visitSearch ||
        (v.clientName && v.clientName.toLowerCase().includes(visitSearch.toLowerCase())) ||
        (v.phone && v.phone.includes(visitSearch)) ||
        (v.location && v.location.toLowerCase().includes(visitSearch.toLowerCase())) ||
        (v.visitType && v.visitType.toLowerCase().includes(visitSearch.toLowerCase()));

      const isVerified = v.verified === true || v.verified === 1 || String(v.verified) === 'true';
      const matchTab =
        visitTab === 'ALL' ||
        (visitTab === 'VERIFIED' && isVerified) ||
        (visitTab === 'PENDING' && !isVerified);

      return matchSearch && matchTab;
    });
  }, [visits, visitSearch, visitTab]);

  // Filtered Orders
  const filteredOrders = useMemo(() => {
    return orders.filter((o) => {
      const matchSearch =
        !orderSearch ||
        String(o.id).includes(orderSearch) ||
        (o.phone && o.phone.includes(orderSearch)) ||
        (o.remarks && o.remarks.toLowerCase().includes(orderSearch.toLowerCase())) ||
        (o.paymentMethod && o.paymentMethod.toLowerCase().includes(orderSearch.toLowerCase()));

      const method = (o.paymentMethod || '').toUpperCase();
      const matchTab =
        orderTab === 'ALL' ||
        (orderTab === 'ONLINE' && (method.includes('ONLINE') || method.includes('UPI'))) ||
        (orderTab === 'CASH' && method.includes('CASH')) ||
        (orderTab === 'CREDIT' && method.includes('CREDIT'));

      return matchSearch && matchTab;
    });
  }, [orders, orderSearch, orderTab]);

  // -------------------------------------------------------------
  // VISIT FORM LOGIC (MATCHING SS1 100%)
  // -------------------------------------------------------------
  const handleOpenVisitModal = async (presetClient = null, jobId = null) => {
    setActiveJobId(jobId || null);
    visitForm.resetFields();
    setVisitSignatureFile(null);
    setVisitGeo({ lat: null, lng: null, address: '' });
    setOtpSent(false);
    setPhoneError('');
    setVisitModalVisible(true);

    const defaultStaff = currentUser?.name || currentUser?.username || 'Ramesh Das';

    if (presetClient) {
      visitForm.setFieldsValue({
        salesPerson: defaultStaff,
        clientName: presetClient.name || '',
        phone: presetClient.phone || '',
        clientType: presetClient.clientType || 'Retailer',
        visitType: 'Follow-up',
        location: presetClient.location || '',
        visitDate: dayjs()
      });
    } else {
      visitForm.setFieldsValue({
        salesPerson: defaultStaff,
        visitType: 'Follow-up',
        clientType: 'Retailer',
        visitDate: dayjs()
      });
    }

    const geo = await captureGeo();
    setVisitGeo(geo);
    if (geo.address && !visitForm.getFieldValue('location')) {
      visitForm.setFieldsValue({ location: geo.address });
    }
  };

  // Pick client handler (SS1 "Pick" button)
  const handlePickClient = (target) => {
    setClientPickerTarget(target);
    setClientPickerSearch('');
    setClientPickerVisible(true);
  };

  const handleSelectPickedClient = (client) => {
    if (clientPickerTarget === 'visit') {
      visitForm.setFieldsValue({
        clientName: client.name || '',
        phone: client.phone || '',
        clientType: client.clientType || 'Retailer',
        location: client.location || ''
      });
    } else {
      orderForm.setFieldsValue({
        clientName: client.name || '',
        phone: client.phone || ''
      });
    }
    setClientPickerVisible(false);
  };

  // Send OTP handler (SS1 "Send OTP" button)
  const handleSendClientOtp = async () => {
    const phone = visitForm.getFieldValue('phone');
    if (!phone || String(phone).trim().length !== 10) {
      setPhoneError('cant be more than 10 digit');
      message.error('Please enter a valid 10-digit phone number');
      return;
    }

    try {
      setSendingOtp(true);
      const res = await api.post('/sales/send-client-otp', { phone });
      if (res.data?.success) {
        setOtpSent(true);
        const otpInfo = res.data?.otp ? ` (OTP: ${res.data.otp})` : '';
        message.success(`OTP sent to ${phone}${otpInfo}`);
      } else {
        message.error(res.data?.message || 'Failed to send OTP');
      }
    } catch (e) {
      console.error('Send OTP error:', e);
      message.error('Failed to send OTP');
    } finally {
      setSendingOtp(false);
    }
  };

  // Submit Visit Form (matching VisitFormScreen.js onSubmit)
  const handleSubmitVisit = async (values) => {
    try {
      if (!values.clientName?.trim()) {
        message.error('Please enter client name');
        return;
      }
      if (!values.phone?.trim()) {
        message.error('Please enter phone number');
        return;
      }
      if (!values.location?.trim()) {
        message.error('Please enter location');
        return;
      }

      setVisitSubmitting(true);
      const form = new FormData();

      form.append('salesPerson', values.salesPerson || currentUser?.name || 'Ramesh Das');
      form.append('visitType', values.visitType || 'Follow-up');
      form.append('clientName', values.clientName.trim());
      form.append('phone', values.phone.trim());
      form.append('clientType', values.clientType || 'Retailer');
      form.append('location', values.location.trim());
      form.append('visitDate', values.visitDate ? values.visitDate.toISOString() : new Date().toISOString());

      if (values.clientOtp && String(values.clientOtp).trim().length > 0) {
        form.append('clientOtp', String(values.clientOtp).trim());
      }

      if (visitGeo.lat && visitGeo.lng) {
        form.append('checkInLat', String(visitGeo.lat));
        form.append('checkInLng', String(visitGeo.lng));
        form.append('checkInAddress', visitGeo.address || values.location || '');
      }

      if (activeJobId) {
        form.append('assignedJobId', String(activeJobId));
      }

      if (visitSignatureFile) {
        form.append('clientSignature', visitSignatureFile);
      }

      const res = await api.post('/sales/visit', form, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (res.data?.success) {
        message.success('Visit submitted successfully.');
        setVisitModalVisible(false);
        setActiveJobId(null);
        loadSalesData();
      } else {
        message.error(res.data?.message || 'Failed to submit visit');
      }
    } catch (err) {
      console.error('Submit visit error:', err);
      message.error(err.response?.data?.message || 'Failed to submit visit.');
    } finally {
      setVisitSubmitting(false);
    }
  };

  // -------------------------------------------------------------
  // ORDER FORM LOGIC (Matching OrderFormScreen.js 100%)
  // -------------------------------------------------------------
  const orderTotals = useMemo(() => {
    const net = orderItems.reduce((sum, it) => sum + (Number(it.qty) || 0) * (Number(it.price) || 0), 0);
    const gst = Math.round(net * 0.18);
    const total = net + gst;
    return { net, gst, total };
  }, [orderItems]);

  const handleOpenOrderModal = async (presetClient = null, jobId = null) => {
    setActiveJobId(jobId || null);
    orderForm.resetFields();
    setOrderProofFile(null);
    setOrderItems([]);
    setOrderGeo({ lat: null, lng: null, address: '' });
    setOrderModalVisible(true);

    if (presetClient) {
      orderForm.setFieldsValue({
        clientName: presetClient.name || '',
        phone: presetClient.phone || '',
        paymentMethod: 'Cash',
        orderDate: dayjs()
      });
    } else {
      orderForm.setFieldsValue({
        paymentMethod: 'Cash',
        orderDate: dayjs()
      });
    }

    const geo = await captureGeo();
    setOrderGeo(geo);
  };

  const handleAddMasterProduct = (prodId) => {
    const p = productsMaster.find((x) => x.id === prodId);
    if (!p) return;
    const nextId = (orderItems[orderItems.length - 1]?.id || 0) + 1;
    setOrderItems((prev) => [
      ...prev,
      {
        id: nextId,
        name: p.name || '',
        size: p.size || 'Standard',
        qty: Number(p.defaultQty || 1),
        price: Number(p.defaultPrice || 0)
      }
    ]);
    message.success(`${p.name} added`);
  };

  const handleAddCustomProduct = (values) => {
    const nextId = (orderItems[orderItems.length - 1]?.id || 0) + 1;
    setOrderItems((prev) => [
      ...prev,
      {
        id: nextId,
        name: values.name.trim(),
        size: values.size ? values.size.trim() : 'Standard',
        qty: Number(values.qty || 1),
        price: Number(values.price || 0)
      }
    ]);
    setCustomProductModalVisible(false);
    customProductForm.resetFields();
    message.success(`${values.name} added`);
  };

  const handleUpdateOrderItem = (id, field, val) => {
    setOrderItems((prev) =>
      prev.map((it) => (it.id === id ? { ...it, [field]: val } : it))
    );
  };

  const handleRemoveOrderItem = (id) => {
    setOrderItems((prev) => prev.filter((it) => it.id !== id));
  };

  const handleSubmitOrder = async (values) => {
    try {
      if (!values.clientName?.trim()) {
        message.error('Client name is required');
        return;
      }
      if (orderItems.length === 0) {
        message.error('Please add at least one product to the order');
        return;
      }
      if (!orderProofFile) {
        message.error('Upload proof is required');
        return;
      }

      setOrderSubmitting(true);
      const form = new FormData();

      form.append('phone', values.phone || '');
      form.append('paymentMethod', values.paymentMethod || 'Cash');
      form.append('remarks', values.remarks || '');
      form.append('orderDate', values.orderDate ? values.orderDate.toISOString() : new Date().toISOString());

      const items = orderItems.map((p) => ({
        name: p.name,
        size: p.size,
        qty: Number(p.qty) || 0,
        price: Number(p.price) || 0
      }));
      form.append('items', JSON.stringify(items));

      if (orderGeo.lat && orderGeo.lng) {
        form.append('checkInLat', String(orderGeo.lat));
        form.append('checkInLng', String(orderGeo.lng));
        form.append('checkInAddress', orderGeo.address || '');
      }

      if (activeJobId) {
        form.append('assignedJobId', String(activeJobId));
      }

      if (orderProofFile) {
        form.append('proof', orderProofFile);
      }

      const res = await api.post('/sales/orders', form, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (res.data?.success) {
        message.success('Order submitted');
        setOrderModalVisible(false);
        setActiveJobId(null);
        loadSalesData();
      } else {
        message.error(res.data?.message || 'Failed to submit order');
      }
    } catch (err) {
      console.error('Submit order error:', err);
      message.error(err.response?.data?.message || 'Failed to submit order');
    } finally {
      setOrderSubmitting(false);
    }
  };

  // -------------------------------------------------------------
  // ASSIGNED JOB ACTIONS (Matching APK AssignedJobDetailScreen)
  // -------------------------------------------------------------
  const handleOpenJobDetail = async (job) => {
    setSelectedJob(job);
    setJobDetailModalVisible(true);
    try {
      const res = await api.get(`/sales/assigned-jobs/${job.id}`);
      if (res.data?.success && res.data.job) {
        setSelectedJob(res.data.job);
      }
    } catch (_) {}
  };

  const handleStartJob = async (jobId) => {
    try {
      setJobActionLoading(true);
      const geo = await captureGeo();
      if (!geo.lat && !geo.lng) {
        message.info('Starting without GPS. Enable location to store coordinates.');
      }
      const res = await api.put(`/sales/assigned-jobs/${jobId}/status`, {
        status: 'inprogress',
        startLat: geo.lat || undefined,
        startLng: geo.lng || undefined,
        startAccuracy: undefined
      });
      if (res.data?.success) {
        message.success('Job started');
        if (res.data.job) {
          setSelectedJob(res.data.job);
        } else {
          setSelectedJob((prev) => (prev ? { ...prev, status: 'inprogress', startedAt: new Date().toISOString() } : null));
        }
        loadSalesData();
      } else {
        message.error(res.data?.message || 'Unable to start job');
      }
    } catch (err) {
      console.error('Start job error:', err);
      message.error(err.response?.data?.message || 'Unable to start job');
    } finally {
      setJobActionLoading(false);
    }
  };

  const handleStopJob = async (jobId) => {
    try {
      setJobActionLoading(true);
      const geo = await captureGeo();
      if (!geo.lat && !geo.lng) {
        message.info('Stopping without GPS. Enable location to store coordinates.');
      }
      const res = await api.put(`/sales/assigned-jobs/${jobId}/status`, {
        status: 'inprogress',
        stopJob: true,
        endLat: geo.lat || undefined,
        endLng: geo.lng || undefined,
        endAccuracy: undefined
      });
      if (res.data?.success) {
        message.success('Job stopped');
        if (res.data.job) {
          setSelectedJob(res.data.job);
        } else {
          setSelectedJob((prev) => (prev ? { ...prev, endedAt: new Date().toISOString() } : null));
        }
        loadSalesData();
      } else {
        message.error(res.data?.message || 'Unable to stop job');
      }
    } catch (err) {
      console.error('Stop job error:', err);
      message.error(err.response?.data?.message || 'Unable to stop job');
    } finally {
      setJobActionLoading(false);
    }
  };

  const handleNavigateJob = (job) => {
    const lat = job?.clientLat;
    const lng = job?.clientLng;
    const addr = job?.clientAddress || job?.client?.location || job?.address;
    if (lat && lng) {
      window.open(`https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`, '_blank');
    } else if (addr) {
      window.open(`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(addr)}`, '_blank');
    } else {
      message.info('No address or coordinates available for navigation.');
    }
  };

  return (
    <StaffLayout>
      {/* 0. TOP HERO / HEADER */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: 16,
        marginBottom: 20
      }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{
              width: 44,
              height: 44,
              borderRadius: 12,
              background: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#fff',
              fontSize: 22,
              boxShadow: '0 8px 16px -4px rgba(37, 99, 235, 0.4)'
            }}>
              <ShopOutlined />
            </div>
            <div>
              <Title level={3} style={{ margin: 0, fontWeight: 700, color: '#0f172a', letterSpacing: '-0.02em' }}>
                Field Sales & Client Portal
              </Title>
              <Text type="secondary" style={{ fontSize: 13 }}>
                Customer visits, product orders, targets, and reporting — identical to Mobile App
              </Text>
            </div>
          </div>
        </div>

        {/* Primary Action Buttons */}
        <Space size="middle" wrap>
          <DatePicker
            value={selectedReportDate}
            onChange={(d) => d && setSelectedReportDate(d)}
            format="DD-MM-YYYY dddd"
            allowClear={false}
            style={{ borderRadius: 8, height: 38, fontWeight: 500 }}
          />
          <Button
            type="default"
            icon={<SyncOutlined spin={loading} />}
            onClick={() => {
              loadSalesData();
              loadTargetAndIncentive(targetPeriod);
            }}
            style={{ borderRadius: 8, fontWeight: 500 }}
          >
            Refresh
          </Button>
          <Button
            type="default"
            icon={<FileTextOutlined style={{ color: '#D97706' }} />}
            onClick={() => navigate('/staff/expenses')}
            style={{ borderRadius: 8, fontWeight: 500 }}
          >
            Expenses
          </Button>
          <Button
            type="default"
            icon={<EnvironmentOutlined style={{ color: '#059669' }} />}
            onClick={() => handleOpenVisitModal()}
            style={{ borderRadius: 8, fontWeight: 600, borderColor: '#059669', color: '#059669' }}
          >
            + Log Visit
          </Button>
          <Button
            type="primary"
            icon={<ShoppingCartOutlined />}
            onClick={() => handleOpenOrderModal()}
            style={{
              borderRadius: 8,
              fontWeight: 600,
              background: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)',
              borderColor: '#2563eb',
              boxShadow: '0 4px 12px 0 rgba(37, 99, 235, 0.35)'
            }}
          >
            + Create Order
          </Button>
        </Space>
      </div>

      {/* 1. TOP KPI SUMMARY ROW (Matching SalesScreen.js summary card) */}
      <Row gutter={[18, 18]} style={{ marginBottom: 24 }}>
        {/* Total Sales Amount */}
        <Col xs={24} sm={12} lg={6}>
          <Card
            hoverable
            style={{
              borderRadius: 16,
              height: '100%',
              border: '1px solid #e2e8f0',
              boxShadow: '0 4px 14px 0 rgba(0, 0, 0, 0.04)'
            }}
            bodyStyle={{ padding: '20px 22px' }}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
              <span style={{ fontSize: 12, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                Total Sales Amount
              </span>
              <div style={{
                width: 38,
                height: 38,
                borderRadius: 10,
                background: '#ecfdf5',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#059669'
              }}>
                <ShopOutlined style={{ fontSize: 18 }} />
              </div>
            </div>
            <div style={{ fontSize: 26, fontWeight: 800, color: '#059669', lineHeight: 1 }}>
              {fmtCurrency(summary.totalAmount)}
            </div>
            <div style={{ marginTop: 12, fontSize: 12, color: '#64748b' }}>
              Avg Order Value: <strong style={{ color: '#0f172a' }}>{fmtCurrency(summary.avgOrderValue)}</strong>
            </div>
          </Card>
        </Col>

        {/* Total Orders */}
        <Col xs={24} sm={12} lg={6}>
          <Card
            hoverable
            style={{
              borderRadius: 16,
              height: '100%',
              border: '1px solid #e2e8f0',
              boxShadow: '0 4px 14px 0 rgba(0, 0, 0, 0.04)'
            }}
            bodyStyle={{ padding: '20px 22px' }}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
              <span style={{ fontSize: 12, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                Total Orders
              </span>
              <div style={{
                width: 38,
                height: 38,
                borderRadius: 10,
                background: '#eff6ff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#2563eb'
              }}>
                <ShoppingCartOutlined style={{ fontSize: 18 }} />
              </div>
            </div>
            <div style={{ fontSize: 28, fontWeight: 800, color: '#1e293b', lineHeight: 1 }}>
              {summary.totalOrders || orders.length} Orders
            </div>
            <div style={{ marginTop: 12, fontSize: 12, color: '#64748b' }}>
              Conversion Rate: <strong style={{ color: '#2563eb' }}>{summary.conversionRate || 0}%</strong>
            </div>
          </Card>
        </Col>

        {/* Party Visits Logged */}
        <Col xs={24} sm={12} lg={6}>
          <Card
            hoverable
            style={{
              borderRadius: 16,
              height: '100%',
              border: '1px solid #e2e8f0',
              boxShadow: '0 4px 14px 0 rgba(0, 0, 0, 0.04)'
            }}
            bodyStyle={{ padding: '20px 22px' }}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
              <span style={{ fontSize: 12, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                Customer Visits
              </span>
              <div style={{
                width: 38,
                height: 38,
                borderRadius: 10,
                background: '#fffbeb',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#d97706'
              }}>
                <EnvironmentOutlined style={{ fontSize: 18 }} />
              </div>
            </div>
            <div style={{ fontSize: 28, fontWeight: 800, color: '#1e293b', lineHeight: 1 }}>
              {visits.length} Visits
            </div>
            <div style={{ marginTop: 12, fontSize: 12, color: '#64748b' }}>
              Verified: <strong style={{ color: '#059669' }}>{visits.filter((v) => v.verified).length}</strong> • Pending: <strong style={{ color: '#d97706' }}>{visits.filter((v) => !v.verified).length}</strong>
            </div>
          </Card>
        </Col>

        {/* Daily Target & Remaining */}
        <Col xs={24} sm={12} lg={6}>
          <Card
            hoverable
            style={{
              borderRadius: 16,
              height: '100%',
              border: '1px solid #e2e8f0',
              boxShadow: '0 4px 14px 0 rgba(0, 0, 0, 0.04)'
            }}
            bodyStyle={{ padding: '20px 22px' }}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
              <span style={{ fontSize: 12, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                Target: {fmtCurrency(targetData.targetAmount)}
              </span>
              <div style={{
                width: 38,
                height: 38,
                borderRadius: 10,
                background: '#f5f3ff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#7c3aed'
              }}>
                <TrophyOutlined style={{ fontSize: 18 }} />
              </div>
            </div>
            <div style={{ fontSize: 22, fontWeight: 800, color: '#7c3aed', lineHeight: 1 }}>
              {targetPercent}% Achieved
            </div>
            <div style={{ marginTop: 8 }}>
              <Progress
                percent={targetPercent}
                strokeColor={{ '0%': '#7c3aed', '100%': '#a855f7' }}
                showInfo={false}
                strokeWidth={6}
              />
            </div>
            <div style={{ marginTop: 8, fontSize: 11, color: '#64748b', textAlign: 'right' }}>
              Remaining: <strong>{fmtCurrency(Math.max(0, targetData.targetAmount - summary.totalAmount))}</strong>
            </div>
          </Card>
        </Col>
      </Row>

      {/* 2. MAIN TABS NAVIGATION */}
      <Card
        style={{ borderRadius: 16, border: '1px solid #e2e8f0', boxShadow: '0 4px 14px 0 rgba(0, 0, 0, 0.04)' }}
        bodyStyle={{ padding: '16px 20px' }}
      >
        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          type="line"
          tabBarStyle={{ marginBottom: 20, fontWeight: 600 }}
          items={[
            {
              key: 'overview',
              label: (
                <span>
                  <TrophyOutlined /> Targets & Incentives
                </span>
              ),
              children: (
                <div>
                  {/* Period Switcher (Daily, Weekly, Monthly - matching TargetsScreen.js) */}
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    background: '#f8fafc',
                    padding: '14px 18px',
                    borderRadius: 12,
                    marginBottom: 20,
                    border: '1px solid #e2e8f0'
                  }}>
                    <div>
                      <Text strong style={{ fontSize: 14 }}>Select Target Period:</Text>
                      <div style={{ color: '#64748b', fontSize: 12, marginTop: 2 }}>
                        Track your daily, weekly, and monthly progress. Aim to close deals by meeting the targets.
                      </div>
                    </div>
                    <Space>
                      {['daily', 'weekly', 'monthly'].map((p) => (
                        <Button
                          key={p}
                          type={targetPeriod === p ? 'primary' : 'default'}
                          onClick={() => setTargetPeriod(p)}
                          style={{
                            borderRadius: 16,
                            padding: '0 18px',
                            fontWeight: 600,
                            textTransform: 'capitalize'
                          }}
                        >
                          {p}
                        </Button>
                      ))}
                    </Space>
                  </div>

                  <Row gutter={[20, 20]} style={{ marginBottom: 24 }}>
                    {/* Progress Card (Matching TargetsScreen.js) */}
                    <Col xs={24} md={12}>
                      <Card
                        title={
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <span style={{ fontWeight: 600 }}>Sales Target ({targetPeriod.toUpperCase()})</span>
                            <div style={{
                              width: 32,
                              height: 32,
                              borderRadius: 16,
                              backgroundColor: '#EEF2FF',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              border: '1px solid #E0E7FF'
                            }}>
                              <Text style={{ color: '#1D4ED8', fontWeight: 700, fontSize: 12 }}>
                                {targetPercent}%
                              </Text>
                            </div>
                          </div>
                        }
                        style={{ borderRadius: 14, border: '1px solid #e2e8f0', height: '100%' }}
                      >
                        <div style={{ margin: '14px 0' }}>
                          <Progress
                            percent={targetPercent}
                            strokeColor={{ '0%': '#3b82f6', '100%': '#1d4ed8' }}
                            strokeWidth={10}
                            showInfo={false}
                          />
                        </div>

                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, color: '#475569', marginTop: 12 }}>
                          <div>
                            Target Amount: <strong>{fmtCurrency(targetData.targetAmount)}</strong>
                          </div>
                          <div>
                            Achieved: <strong style={{ color: '#059669' }}>{fmtCurrency(achievedData.amount)}</strong>
                          </div>
                        </div>

                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, color: '#475569', marginTop: 8 }}>
                          <div>
                            Target Orders: <strong>{targetData.targetOrders || 0}</strong>
                          </div>
                          <div>
                            Achieved Orders: <strong>{achievedData.orders || 0}</strong>
                          </div>
                        </div>

                        <Divider style={{ margin: '14px 0' }} />

                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Text type="secondary" style={{ fontSize: 12 }}>
                            Remaining to Target:
                          </Text>
                          <strong style={{ color: '#d97706', fontSize: 15 }}>
                            {fmtCurrency(Math.max(0, targetData.targetAmount - achievedData.amount))}
                          </strong>
                        </div>
                      </Card>
                    </Col>

                    {/* Incentive Card (Matching TargetsScreen.js incentive card) */}
                    <Col xs={24} md={12}>
                      <Card
                        title={
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <GiftOutlined style={{ color: '#059669', fontSize: 18 }} />
                            <span style={{ fontWeight: 600 }}>Incentive Scheme</span>
                          </div>
                        }
                        style={{ borderRadius: 14, border: '1px solid #e2e8f0', height: '100%' }}
                      >
                        <div style={{
                          background: '#F0FDF4',
                          borderRadius: 12,
                          border: '1px solid #BBF7D0',
                          padding: '16px 18px',
                          marginBottom: 16
                        }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                            <Text strong style={{ fontSize: 14, color: '#166534' }}>
                              {incentiveData?.title || `${targetPeriod.toUpperCase()} Incentive Bonus`}
                            </Text>
                            <Tag color="green" style={{ borderRadius: 6, fontWeight: 700 }}>
                              Bonus: {fmtCurrency(incentiveData?.rewardAmount || 0)}
                            </Tag>
                          </div>
                          <div style={{ fontSize: 12, color: '#15803d', lineHeight: 1.6 }}>
                            {incentiveData?.ordersThreshold
                              ? `Reach ${incentiveData.ordersThreshold} orders in ${targetPeriod} cycle to unlock bonus.`
                              : `Reach 100% of the sales target to become eligible for incentive rewards.`}
                          </div>
                        </div>

                        {/* Weekly Sales Trend Chart (Matching SalesReportScreen.js) */}
                        <div style={{ marginTop: 10 }}>
                          <div style={{ fontSize: 12, fontWeight: 600, color: '#475569', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6 }}>
                            <BarChartOutlined style={{ color: '#2563eb' }} />
                            Weekly Sales Distribution (Current Week)
                          </div>
                          <ResponsiveContainer width="100%" height={160}>
                            <BarChart data={weeklySalesData} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
                              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                              <XAxis dataKey="day" stroke="#94a3b8" fontSize={11} tickLine={false} />
                              <YAxis stroke="#94a3b8" fontSize={11} tickLine={false} axisLine={false} />
                              <RechartsTooltip
                                formatter={(val) => [fmtCurrency(val), 'Sales']}
                                contentStyle={{ borderRadius: 8, background: '#0f172a', color: '#fff', fontSize: 12 }}
                              />
                              <Bar dataKey="sales" fill="#3b82f6" radius={[4, 4, 0, 0]} barSize={20} />
                            </BarChart>
                          </ResponsiveContainer>
                        </div>
                      </Card>
                    </Col>
                  </Row>
                </div>
              )
            },
            {
              key: 'visits',
              label: (
                <span>
                  <EnvironmentOutlined /> Party Visits ({visits.length})
                </span>
              ),
              children: (
                <div>
                  {/* Visits Filter Bar */}
                  <Row gutter={[16, 16]} style={{ marginBottom: 16 }} align="middle" justify="space-between">
                    <Col xs={24} md={12}>
                      <Space wrap>
                        <Tabs
                          activeKey={visitTab}
                          onChange={setVisitTab}
                          size="small"
                          items={[
                            { key: 'ALL', label: `All Visits (${visits.length})` },
                            { key: 'VERIFIED', label: `Verified (${visits.filter(v => v.verified).length})` },
                            { key: 'PENDING', label: `Pending (${visits.filter(v => !v.verified).length})` }
                          ]}
                        />
                      </Space>
                    </Col>
                    <Col xs={24} md={12} style={{ textAlign: 'right' }}>
                      <Space wrap>
                        <Input
                          placeholder="Search client, phone, location..."
                          prefix={<SearchOutlined style={{ color: '#94a3b8' }} />}
                          value={visitSearch}
                          onChange={(e) => setVisitSearch(e.target.value)}
                          style={{ width: 240, borderRadius: 8 }}
                          allowClear
                        />
                        <Button
                          type="primary"
                          icon={<PlusOutlined />}
                          onClick={() => handleOpenVisitModal()}
                          style={{ borderRadius: 8, background: '#059669', borderColor: '#059669' }}
                        >
                          Log Visit
                        </Button>
                      </Space>
                    </Col>
                  </Row>

                  {/* Visits Table */}
                  <Table
                    dataSource={filteredVisits}
                    rowKey="id"
                    loading={loading}
                    pagination={{ pageSize: 10, showTotal: (t) => `Total ${t} visits` }}
                    columns={[
                      {
                        title: 'Date & Time',
                        dataIndex: 'visitDate',
                        key: 'visitDate',
                        width: 140,
                        render: (d) => (
                          <div>
                            <div style={{ fontWeight: 600 }}>{dayjs(d).format('DD MMM YYYY')}</div>
                            <div style={{ fontSize: 11, color: '#64748b' }}>{dayjs(d).format('hh:mm A')}</div>
                          </div>
                        )
                      },
                      {
                        title: 'Client Name',
                        dataIndex: 'clientName',
                        key: 'clientName',
                        render: (name, r) => (
                          <div>
                            <Text strong style={{ fontSize: 13 }}>{name || 'Client'}</Text>
                            {r.phone && (
                              <div style={{ fontSize: 11, color: '#64748b', display: 'flex', alignItems: 'center', gap: 4 }}>
                                <PhoneOutlined /> <a href={`tel:${r.phone}`}>{r.phone}</a>
                              </div>
                            )}
                          </div>
                        )
                      },
                      {
                        title: 'Visit Type',
                        dataIndex: 'visitType',
                        key: 'visitType',
                        render: (vt) => {
                          const col =
                            vt === 'New Visit' ? 'blue' :
                            vt === 'Complaint' ? 'volcano' :
                            vt === 'Demo' ? 'purple' : 'cyan';
                          return <Tag color={col} style={{ borderRadius: 4 }}>{vt || 'Follow-up'}</Tag>;
                        }
                      },
                      {
                        title: 'Client Type',
                        dataIndex: 'clientType',
                        key: 'clientType',
                        render: (ct) => <Tag style={{ borderRadius: 4 }}>{ct || 'Retailer'}</Tag>
                      },
                      {
                        title: 'Location & Map',
                        dataIndex: 'location',
                        key: 'location',
                        render: (loc, r) => {
                          const hasCoords = r.checkInLat && r.checkInLng;
                          return (
                            <div>
                              <div style={{ fontSize: 12, maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                <EnvironmentOutlined style={{ color: '#2563eb', marginRight: 4 }} />
                                {loc || r.checkInAddress || 'Location Captured'}
                              </div>
                              {hasCoords && (
                                <a
                                  href={`https://www.google.com/maps?q=${r.checkInLat},${r.checkInLng}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  style={{ fontSize: 11, color: '#2563eb', fontWeight: 600 }}
                                >
                                  View on Google Maps ↗
                                </a>
                              )}
                            </div>
                          );
                        }
                      },
                      {
                        title: 'Verification',
                        dataIndex: 'verified',
                        key: 'verified',
                        render: (v) => {
                          const isVer = v === true || v === 1 || String(v) === 'true';
                          return isVer ? (
                            <Tag color="success" icon={<CheckCircleOutlined />} style={{ borderRadius: 4 }}>
                              Verified
                            </Tag>
                          ) : (
                            <Tag color="warning" icon={<ClockCircleOutlined />} style={{ borderRadius: 4 }}>
                              Pending
                            </Tag>
                          );
                        }
                      },
                      {
                        title: 'Action',
                        key: 'action',
                        width: 90,
                        render: (_, r) => (
                          <Button
                            size="small"
                            type="link"
                            icon={<EyeOutlined />}
                            onClick={() => {
                              setSelectedVisit(r);
                              setVisitDetailsVisible(true);
                            }}
                          >
                            Details
                          </Button>
                        )
                      }
                    ]}
                  />
                </div>
              )
            },
            {
              key: 'orders',
              label: (
                <span>
                  <ShoppingCartOutlined /> Orders ({orders.length})
                </span>
              ),
              children: (
                <div>
                  {/* Orders Filter Bar */}
                  <Row gutter={[16, 16]} style={{ marginBottom: 16 }} align="middle" justify="space-between">
                    <Col xs={24} md={12}>
                      <Space wrap>
                        <Tabs
                          activeKey={orderTab}
                          onChange={setOrderTab}
                          size="small"
                          items={[
                            { key: 'ALL', label: `All Orders (${orders.length})` },
                            { key: 'ONLINE', label: 'Online' },
                            { key: 'CASH', label: 'Cash' },
                            { key: 'CREDIT', label: 'Credit / Cheque' }
                          ]}
                        />
                      </Space>
                    </Col>
                    <Col xs={24} md={12} style={{ textAlign: 'right' }}>
                      <Space wrap>
                        <Input
                          placeholder="Search order ID, phone..."
                          prefix={<SearchOutlined style={{ color: '#94a3b8' }} />}
                          value={orderSearch}
                          onChange={(e) => setOrderSearch(e.target.value)}
                          style={{ width: 240, borderRadius: 8 }}
                          allowClear
                        />
                        <Button
                          type="primary"
                          icon={<PlusOutlined />}
                          onClick={() => handleOpenOrderModal()}
                          style={{ borderRadius: 8 }}
                        >
                          New Order
                        </Button>
                      </Space>
                    </Col>
                  </Row>

                  {/* Orders Table */}
                  <Table
                    dataSource={filteredOrders}
                    rowKey="id"
                    loading={loading}
                    pagination={{ pageSize: 10, showTotal: (t) => `Total ${t} orders` }}
                    columns={[
                      {
                        title: 'Order ID',
                        dataIndex: 'id',
                        key: 'id',
                        width: 100,
                        render: (id) => <Text strong style={{ color: '#2563eb' }}>#ORD-{id}</Text>
                      },
                      {
                        title: 'Date',
                        dataIndex: 'orderDate',
                        key: 'orderDate',
                        width: 130,
                        render: (d) => (
                          <div>
                            <div>{dayjs(d).format('DD MMM YYYY')}</div>
                            <div style={{ fontSize: 11, color: '#64748b' }}>{dayjs(d).format('hh:mm A')}</div>
                          </div>
                        )
                      },
                      {
                        title: 'Customer Phone',
                        dataIndex: 'phone',
                        key: 'phone',
                        render: (phone, r) => (
                          <div>
                            <Text strong>{r.client?.name || 'Client'}</Text>
                            <div style={{ fontSize: 11, color: '#64748b' }}>{phone || '--'}</div>
                          </div>
                        )
                      },
                      {
                        title: 'Products Summary',
                        dataIndex: 'items',
                        key: 'items',
                        render: (its) => {
                          const list = Array.isArray(its) ? its : [];
                          return (
                            <div>
                              <Tag color="blue" style={{ borderRadius: 4 }}>
                                {list.length} {list.length === 1 ? 'Item' : 'Items'}
                              </Tag>
                              <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>
                                {list.map(i => `${i.name} (x${i.qty})`).slice(0, 2).join(', ')}
                                {list.length > 2 ? '...' : ''}
                              </div>
                            </div>
                          );
                        }
                      },
                      {
                        title: 'Net Amount',
                        dataIndex: 'netAmount',
                        key: 'netAmount',
                        render: (amt) => fmtCurrency(amt)
                      },
                      {
                        title: 'GST (18%)',
                        dataIndex: 'gstAmount',
                        key: 'gstAmount',
                        render: (gst) => fmtCurrency(gst)
                      },
                      {
                        title: 'Total Amount',
                        dataIndex: 'totalAmount',
                        key: 'totalAmount',
                        render: (amt) => <strong style={{ color: '#059669', fontSize: 14 }}>{fmtCurrency(amt)}</strong>
                      },
                      {
                        title: 'Payment Method',
                        dataIndex: 'paymentMethod',
                        key: 'paymentMethod',
                        render: (pm) => <Tag color="cyan" style={{ borderRadius: 4 }}>{pm || 'Cash'}</Tag>
                      },
                      {
                        title: 'Proof',
                        dataIndex: 'proofUrl',
                        key: 'proofUrl',
                        render: (p) => {
                          if (!p) return <Text type="secondary" style={{ fontSize: 11 }}>No Proof</Text>;
                          const fullUrl = p.startsWith('http') ? p : `${API_BASE_URL}${p}`;
                          return (
                            <a href={fullUrl} target="_blank" rel="noopener noreferrer">
                              <Tag color="purple" style={{ borderRadius: 4, cursor: 'pointer' }}>View Proof</Tag>
                            </a>
                          );
                        }
                      },
                      {
                        title: 'Action',
                        key: 'action',
                        width: 90,
                        render: (_, r) => (
                          <Button
                            size="small"
                            type="link"
                            icon={<EyeOutlined />}
                            onClick={() => {
                              setSelectedOrder(r);
                              setOrderDetailsVisible(true);
                            }}
                          >
                            Details
                          </Button>
                        )
                      }
                    ]}
                  />
                </div>
              )
            },
            {
              key: 'customers',
              label: (
                <span>
                  <TeamOutlined /> Customer Directory ({clientsMaster.length})
                </span>
              ),
              children: (
                <div>
                  <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Text type="secondary">
                      Customer parties assigned to you or visited in the field
                    </Text>
                    <Button
                      type="primary"
                      icon={<PlusOutlined />}
                      onClick={() => handleOpenVisitModal()}
                      style={{ borderRadius: 8 }}
                    >
                      Log New Visit
                    </Button>
                  </div>

                  {clientsMaster.length === 0 ? (
                    <Empty
                      description="No clients found yet. Log a visit to create new customer records!"
                      style={{ padding: '40px 0' }}
                    />
                  ) : (
                    <Table
                      dataSource={clientsMaster}
                      rowKey="id"
                      pagination={{ pageSize: 8 }}
                      columns={[
                        {
                          title: 'Client Name',
                          dataIndex: 'name',
                          key: 'name',
                          render: (name) => <Text strong>{name}</Text>
                        },
                        {
                          title: 'Phone Number',
                          dataIndex: 'phone',
                          key: 'phone',
                          render: (ph) => ph ? <a href={`tel:${ph}`}><PhoneOutlined /> {ph}</a> : '--'
                        },
                        {
                          title: 'Email',
                          dataIndex: 'email',
                          key: 'email',
                          render: (em) => em || '--'
                        },
                        {
                          title: 'Client Type',
                          dataIndex: 'clientType',
                          key: 'clientType',
                          render: (t) => <Tag>{t || 'Retailer'}</Tag>
                        },
                        {
                          title: 'Location',
                          dataIndex: 'location',
                          key: 'location',
                          render: (l) => l || '--'
                        },
                        {
                          title: 'Quick Actions',
                          key: 'actions',
                          render: (_, c) => (
                            <Space>
                              <Button
                                size="small"
                                type="primary"
                                ghost
                                icon={<EnvironmentOutlined />}
                                onClick={() => handleOpenVisitModal(c)}
                              >
                                Visit
                              </Button>
                              <Button
                                size="small"
                                type="default"
                                icon={<ShoppingCartOutlined />}
                                onClick={() => handleOpenOrderModal(c)}
                              >
                                Order
                              </Button>
                            </Space>
                          )
                        }
                      ]}
                    />
                  )}
                </div>
              )
            },
            {
              key: 'jobs',
              label: (
                <span>
                  <AuditOutlined /> Assigned Jobs ({assignedJobs.length})
                </span>
              ),
              children: (
                <div>
                  <div style={{ marginBottom: 16 }}>
                    <Text type="secondary">
                      Tasks and field visits assigned to you by manager / admin. Complete pending tasks on time.
                    </Text>
                  </div>

                  {assignedJobs.length === 0 ? (
                    <Empty description="No assigned jobs pending" style={{ padding: '40px 0' }} />
                  ) : (
                    <Row gutter={[16, 16]}>
                      {assignedJobs.map((job) => {
                        const s = String(job.status || 'pending').toLowerCase();
                        const conf = s === 'complete'
                          ? { bg: '#DCFCE7', border: '#DCFCE7', color: '#166534', label: 'COMPLETE' }
                          : s === 'inprogress'
                          ? { bg: '#E0ECFF', border: '#E0ECFF', color: '#1D4ED8', label: 'INPROGRESS' }
                          : { bg: '#FFF0C7', border: '#FFF0C7', color: '#987500', label: 'PENDING' };

                        const isPending = s === 'pending';
                        const hasEnded = !!job.endedAt;

                        return (
                          <Col xs={24} md={12} lg={8} key={job.id}>
                            <div
                              style={{
                                backgroundColor: '#F9FAFB',
                                borderRadius: 10,
                                padding: '16px',
                                border: '1px solid #E5E7EB',
                                transition: 'all 0.2s ease',
                                cursor: 'pointer',
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'space-between',
                                minHeight: 180,
                                boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
                              }}
                              onClick={() => handleOpenJobDetail(job)}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.borderColor = '#93C5FD';
                                e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.08)';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.borderColor = '#E5E7EB';
                                e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.05)';
                              }}
                            >
                              <div>
                                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8 }}>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                    <EnvironmentOutlined style={{ color: '#125EC9', fontSize: 16 }} />
                                    <span style={{ fontSize: 15, fontWeight: 700, color: '#1F2937' }}>
                                      {job.client?.name || job.client || 'Client'}
                                    </span>
                                  </div>
                                  <span
                                    style={{
                                      backgroundColor: conf.bg,
                                      borderRadius: 4,
                                      padding: '2px 8px',
                                      border: `1px solid ${conf.border}`,
                                      color: conf.color,
                                      fontSize: 10,
                                      fontWeight: 700,
                                      letterSpacing: '0.5px'
                                    }}
                                  >
                                    {conf.label}
                                  </span>
                                </div>

                                <div style={{ fontSize: 12, color: '#4B5563', marginTop: 8, paddingLeft: 24 }}>
                                  {job.clientAddress || job.client?.location || 'Address not specified'}
                                </div>

                                {job.title && (
                                  <div style={{ fontSize: 12, color: '#6B7280', marginTop: 4, paddingLeft: 24 }}>
                                    <Text type="secondary" italic>{job.title}</Text>
                                  </div>
                                )}
                              </div>

                              <div style={{ marginTop: 14, paddingTop: 10, borderTop: '1px solid #F3F4F6' }}>
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, color: '#6B7280' }}>
                                    <ClockCircleOutlined />
                                    <span>Due: {job.dueDate ? dayjs(job.dueDate).format('DD MMM YYYY') : '--'}</span>
                                  </div>
                                  {job.assignedOn && (
                                    <div style={{ fontSize: 11, color: '#9CA3AF' }}>
                                      Assigned: {dayjs(job.assignedOn).format('DD MMM')}
                                    </div>
                                  )}
                                </div>

                                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }} onClick={(e) => e.stopPropagation()}>
                                  <Button
                                    size="small"
                                    type="primary"
                                    ghost
                                    onClick={() => handleOpenJobDetail(job)}
                                    style={{ fontSize: 11 }}
                                  >
                                    View Details
                                  </Button>

                                  {isPending && !job.startedAt && !job.endedAt && (
                                    <Button
                                      size="small"
                                      danger
                                      loading={jobActionLoading}
                                      onClick={() => handleStartJob(job.id)}
                                      style={{ fontSize: 11 }}
                                    >
                                      Start Job
                                    </Button>
                                  )}

                                  {s === 'inprogress' && !hasEnded && (
                                    <Button
                                      size="small"
                                      style={{ color: '#166534', borderColor: '#86EFAC', background: '#DCFCE7', fontSize: 11 }}
                                      loading={jobActionLoading}
                                      onClick={() => handleStopJob(job.id)}
                                    >
                                      Stop Job
                                    </Button>
                                  )}

                                  <Button
                                    size="small"
                                    icon={<GlobalOutlined />}
                                    onClick={() => handleNavigateJob(job)}
                                    style={{ fontSize: 11 }}
                                  >
                                    Navigate
                                  </Button>
                                </div>
                              </div>
                            </div>
                          </Col>
                        );
                      })}
                    </Row>
                  )}
                </div>
              )
            }
          ]}
        />
      </Card>

      {/* ========================================================================= */}
      {/* 3. MODAL: VISIT FORM (MATCHING SS1 SCREENSHOT 100% IDENTICALLY)            */}
      {/* ========================================================================= */}
      <Modal
        title={<span style={{ fontSize: 16, fontWeight: 700, color: '#1F2937' }}>Visit Form</span>}
        open={visitModalVisible}
        onCancel={() => {
          setVisitModalVisible(false);
          setActiveJobId(null);
        }}
        footer={null}
        width={680}
        destroyOnClose
      >
        <Form
          form={visitForm}
          layout="vertical"
          onFinish={handleSubmitVisit}
          style={{ marginTop: 10 }}
        >
          {activeJobId && (
            <Alert
              type="info"
              showIcon
              message={<span>Task linked to Assigned Job <strong>#{activeJobId}</strong></span>}
              style={{ marginBottom: 14, borderRadius: 6 }}
            />
          )}

          {/* SECTION 1: Visit Details (Matching SS1) */}
          <div style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: '#374151', marginBottom: 8 }}>
              Visit Details
            </div>
            <div style={{
              background: '#FFFFFF',
              borderRadius: 8,
              border: '1px solid #E5E7EB',
              padding: '16px 16px 4px 16px'
            }}>
              {/* Visit Date and Time */}
              <Form.Item
                label={<span style={{ color: '#4B5563', fontSize: 12, fontWeight: 500 }}>Visit Date and Time</span>}
                name="visitDate"
                rules={[{ required: true }]}
              >
                <DatePicker
                  showTime
                  format="DD MMM, YY hh:mm A"
                  style={{ width: '100%', borderRadius: 6, height: 40 }}
                  suffixIcon={<CalendarOutlined style={{ color: '#6B7280' }} />}
                />
              </Form.Item>

              {/* Sales Person */}
              <Form.Item
                label={<span style={{ color: '#4B5563', fontSize: 12, fontWeight: 500 }}>Sales Person</span>}
                name="salesPerson"
                rules={[{ required: true, message: 'Please enter sales person name' }]}
              >
                <Input placeholder="Enter name" style={{ borderRadius: 6, height: 40 }} />
              </Form.Item>

              {/* Visit Type */}
              <Form.Item
                label={<span style={{ color: '#4B5563', fontSize: 12, fontWeight: 500 }}>Visit Type</span>}
                name="visitType"
                rules={[{ required: true }]}
              >
                <Select style={{ borderRadius: 6, height: 40 }}>
                  {VISIT_TYPES.map((t) => (
                    <Option key={t} value={t}>{t}</Option>
                  ))}
                </Select>
              </Form.Item>
            </div>
          </div>

          {/* SECTION 2: Client Information (Matching SS1) */}
          <div style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: '#374151', marginBottom: 8 }}>
              Client Information
            </div>
            <div style={{
              background: '#FFFFFF',
              borderRadius: 8,
              border: '1px solid #E5E7EB',
              padding: '16px 16px 4px 16px'
            }}>
              {/* Client Name + Pick Button (Matching SS1) */}
              <Form.Item
                label={<span style={{ color: '#4B5563', fontSize: 12, fontWeight: 500 }}>Client Name</span>}
                name="clientName"
                rules={[{ required: true, message: 'Please enter client name' }]}
              >
                <Input
                  placeholder="Enter client name"
                  style={{ borderRadius: 6, height: 40 }}
                  disabled={!!activeJobId}
                  addonAfter={
                    !activeJobId ? (
                      <span
                        onClick={() => handlePickClient('visit')}
                        style={{
                          cursor: 'pointer',
                          color: '#1D4ED8',
                          fontWeight: 600,
                          fontSize: 12,
                          padding: '0 4px',
                          display: 'block'
                        }}
                      >
                        Pick
                      </span>
                    ) : null
                  }
                />
              </Form.Item>

              {/* Phone Number + Send OTP Button (Matching SS1) */}
              <Form.Item
                label={<span style={{ color: '#4B5563', fontSize: 12, fontWeight: 500 }}>Phone Number</span>}
                name="phone"
                rules={[{ required: true, message: 'Please enter phone number' }]}
                validateStatus={phoneError ? 'error' : ''}
                help={phoneError ? <span style={{ color: '#F24E43', fontSize: 11 }}>{phoneError}</span> : null}
              >
                <Input
                  placeholder="Enter phone"
                  maxLength={11}
                  disabled={!!activeJobId}
                  onChange={(e) => {
                    const cleaned = e.target.value.replace(/[^0-9]/g, '');
                    if (cleaned.length > 10) {
                      setPhoneError('cant be more than 10 digit');
                    } else {
                      setPhoneError('');
                    }
                  }}
                  style={{ borderRadius: 6, height: 40 }}
                  addonAfter={
                    <span
                      onClick={sendingOtp ? null : handleSendClientOtp}
                      style={{
                        cursor: sendingOtp ? 'not-allowed' : 'pointer',
                        color: sendingOtp ? '#9CA3AF' : '#1D4ED8',
                        fontWeight: 600,
                        fontSize: 12,
                        padding: '0 4px',
                        display: 'block'
                      }}
                    >
                      {sendingOtp ? 'Sending...' : (otpSent ? 'Resend OTP' : 'Send OTP')}
                    </span>
                  }
                />
              </Form.Item>

              {/* Client Type */}
              <Form.Item
                label={<span style={{ color: '#4B5563', fontSize: 12, fontWeight: 500 }}>Client Type</span>}
                name="clientType"
                rules={[{ required: true }]}
              >
                <Select style={{ borderRadius: 6, height: 40 }}>
                  {CLIENT_TYPES.map((ct) => (
                    <Option key={ct} value={ct}>{ct}</Option>
                  ))}
                </Select>
              </Form.Item>

              {/* Location */}
              <Form.Item
                label={<span style={{ color: '#4B5563', fontSize: 12, fontWeight: 500 }}>Location</span>}
                name="location"
                rules={[{ required: true, message: 'Please enter location' }]}
              >
                <Input placeholder="Enter location" style={{ borderRadius: 6, height: 40 }} />
              </Form.Item>
            </div>
          </div>

          {/* SECTION 3: Client Verification (Matching SS1) */}
          <div style={{ marginBottom: 20 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: '#374151', marginBottom: 8 }}>
              Client Verification
            </div>
            <div style={{
              background: '#FFFFFF',
              borderRadius: 8,
              border: '1px solid #E5E7EB',
              padding: '16px 16px 4px 16px'
            }}>
              {/* Client OTP (Optional) */}
              <Form.Item
                label={<span style={{ color: '#4B5563', fontSize: 12, fontWeight: 500 }}>Client OTP (Optional)</span>}
                name="clientOtp"
              >
                <Input placeholder="Enter OTP" maxLength={6} style={{ borderRadius: 6, height: 40 }} />
              </Form.Item>

              {/* Client Signature (Photo) */}
              <Form.Item
                label={<span style={{ color: '#4B5563', fontSize: 12, fontWeight: 500 }}>Client Signature (Photo)</span>}
              >
                <Upload
                  beforeUpload={(file) => {
                    setVisitSignatureFile(file);
                    return false;
                  }}
                  maxCount={1}
                  onRemove={() => setVisitSignatureFile(null)}
                >
                  <Button icon={<UploadOutlined />} style={{ borderRadius: 6 }}>
                    Upload File
                  </Button>
                </Upload>
              </Form.Item>
            </div>
          </div>

          {/* FOOTER BUTTONS: Cancel and Save Visit (Matching SS1) */}
          <div style={{ display: 'flex', gap: 12, marginTop: 16 }}>
            <Button
              onClick={() => setVisitModalVisible(false)}
              style={{
                flex: 1,
                height: 42,
                borderRadius: 8,
                borderColor: '#125EC9',
                color: '#125EC9',
                fontWeight: 600
              }}
            >
              Cancel
            </Button>
            <Button
              type="primary"
              htmlType="submit"
              loading={visitSubmitting}
              style={{
                flex: 1,
                height: 42,
                borderRadius: 8,
                background: '#0059D7',
                borderColor: '#0059D7',
                fontWeight: 600
              }}
            >
              Save Visit
            </Button>
          </div>
        </Form>
      </Modal>

      {/* ========================================================================= */}
      {/* 4. MODAL: CREATE ORDER FORM (MATCHING OrderFormScreen.js 100%)             */}
      {/* ========================================================================= */}
      <Modal
        title={<span style={{ fontSize: 16, fontWeight: 700, color: '#1F2937' }}>Order Form</span>}
        open={orderModalVisible}
        onCancel={() => {
          setOrderModalVisible(false);
          setActiveJobId(null);
        }}
        footer={null}
        width={720}
        destroyOnClose
      >
        <Form
          form={orderForm}
          layout="vertical"
          onFinish={handleSubmitOrder}
          style={{ marginTop: 10 }}
        >
          {activeJobId && (
            <Alert
              type="info"
              showIcon
              message={<span>Order linked to Assigned Job <strong>#{activeJobId}</strong></span>}
              style={{ marginBottom: 12, borderRadius: 6 }}
            />
          )}

          <div style={{ color: '#6B7280', fontSize: 12, marginBottom: 12 }}>
            Create or update customer order details and attach proof.
          </div>

          {/* Client Info Card */}
          <div style={{
            background: '#FFFFFF',
            borderRadius: 8,
            border: '1px solid #E5E7EB',
            padding: '16px 16px 4px 16px',
            marginBottom: 12
          }}>
            <Form.Item
              label={<span style={{ color: '#4B5563', fontSize: 12, fontWeight: 500 }}>Client name *</span>}
              name="clientName"
              rules={[{ required: true, message: 'Client name is required' }]}
            >
              <Input
                placeholder="Enter client name"
                style={{ borderRadius: 6, height: 40 }}
                disabled={!!activeJobId}
                addonAfter={
                  !activeJobId ? (
                    <span
                      onClick={() => handlePickClient('order')}
                      style={{
                        cursor: 'pointer',
                        color: '#1D4ED8',
                        fontWeight: 600,
                        fontSize: 12,
                        padding: '0 4px',
                        display: 'block'
                      }}
                    >
                      Pick
                    </span>
                  ) : null
                }
              />
            </Form.Item>

            <Form.Item
              label={<span style={{ color: '#4B5563', fontSize: 12, fontWeight: 500 }}>Phone number</span>}
              name="phone"
            >
              <Input placeholder="Enter phone" disabled={!!activeJobId} style={{ borderRadius: 6, height: 40 }} />
            </Form.Item>
          </div>

          {/* Order date and time */}
          <div style={{
            background: '#FFFFFF',
            borderRadius: 8,
            border: '1px solid #E5E7EB',
            padding: '16px 16px 4px 16px',
            marginBottom: 12
          }}>
            <Form.Item
              label={<span style={{ color: '#1F2937', fontSize: 12, fontWeight: 600 }}>Order date and time</span>}
              name="orderDate"
              rules={[{ required: true }]}
            >
              <DatePicker
                showTime
                format="ddd, DD-MM-YYYY"
                style={{ width: '100%', borderRadius: 6, height: 40 }}
              />
            </Form.Item>
          </div>

          {/* Products Info Card */}
          <div style={{
            background: '#FFFFFF',
            borderRadius: 8,
            border: '1px solid #E5E7EB',
            padding: '16px',
            marginBottom: 12
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
              <span style={{ color: '#1F2937', fontWeight: 600, fontSize: 13 }}>Products info</span>
              <Button
                type="primary"
                size="small"
                onClick={() => {
                  customProductForm.resetFields();
                  setCustomProductModalVisible(true);
                }}
                style={{ borderRadius: 6, background: '#125EC9', borderColor: '#125EC9' }}
              >
                + Add Manual Product
              </Button>
            </div>

            {/* Select from master list */}
            <div style={{ marginBottom: 14 }}>
              <div style={{ color: '#6B7280', fontSize: 11, fontWeight: 500, marginBottom: 4 }}>
                Select Product from Master List
              </div>
              <Select
                placeholder="Tap here to choose product from list"
                style={{ width: '100%', height: 40 }}
                showSearch
                onChange={handleAddMasterProduct}
                value={null}
              >
                {productsMaster.map((p) => (
                  <Option key={p.id} value={p.id}>
                    {p.name} {p.size ? `(${p.size})` : ''} — ₹{p.defaultPrice}
                  </Option>
                ))}
              </Select>
            </div>

            {/* Added products list */}
            {orderItems.length === 0 ? (
              <div style={{ padding: '10px 0', color: '#6B7280', fontSize: 12 }}>
                No products added to this order.
              </div>
            ) : (
              <div>
                {orderItems.map((p) => {
                  const lineAmt = (Number(p.qty) || 0) * (Number(p.price) || 0);
                  return (
                    <div
                      key={p.id}
                      style={{
                        borderTop: '1px solid #E5E7EB',
                        paddingTop: 10,
                        marginTop: 8
                      }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                        <span style={{ color: '#111827', fontWeight: 600, fontSize: 13 }}>{p.name}</span>
                        <Button
                          type="link"
                          danger
                          size="small"
                          onClick={() => handleRemoveOrderItem(p.id)}
                          style={{ padding: 0 }}
                        >
                          Remove
                        </Button>
                      </div>

                      <Row gutter={12} align="middle">
                        <Col span={6}>
                          <span style={{ fontSize: 11, color: '#6B7280' }}>Product size</span>
                          <Input
                            size="small"
                            value={p.size}
                            onChange={(e) => handleUpdateOrderItem(p.id, 'size', e.target.value)}
                            style={{ borderRadius: 4, marginTop: 2 }}
                          />
                        </Col>
                        <Col span={6}>
                          <span style={{ fontSize: 11, color: '#6B7280' }}>Product qty</span>
                          <InputNumber
                            size="small"
                            min={1}
                            value={p.qty}
                            onChange={(v) => handleUpdateOrderItem(p.id, 'qty', v || 1)}
                            style={{ width: '100%', borderRadius: 4, marginTop: 2 }}
                          />
                        </Col>
                        <Col span={6}>
                          <span style={{ fontSize: 11, color: '#6B7280' }}>Product rate</span>
                          <InputNumber
                            size="small"
                            min={0}
                            value={p.price}
                            onChange={(v) => handleUpdateOrderItem(p.id, 'price', v || 0)}
                            style={{ width: '100%', borderRadius: 4, marginTop: 2 }}
                          />
                        </Col>
                        <Col span={6} style={{ textAlign: 'right' }}>
                          <span style={{ fontSize: 11, color: '#6B7280' }}>Product amount</span>
                          <div style={{ fontWeight: 700, color: '#059669', fontSize: 13, marginTop: 2 }}>
                            {fmtCurrency(lineAmt)}
                          </div>
                        </Col>
                      </Row>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Totals Summary (Matching OrderFormScreen.js) */}
          <div style={{
            background: '#FFFFFF',
            borderTop: '1px solid #E5E7EB',
            padding: '10px 14px',
            marginBottom: 12
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4, fontSize: 12 }}>
              <span style={{ color: '#454545' }}>Net amount :</span>
              <span style={{ color: '#454545', fontWeight: 600 }}>₹ {orderTotals.net}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4, fontSize: 12 }}>
              <span style={{ color: '#454545' }}>GST (18%) :</span>
              <span style={{ color: '#454545', fontWeight: 600 }}>₹ {orderTotals.gst}</span>
            </div>
            <Divider style={{ margin: '6px 0' }} />
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
              <span style={{ color: '#111827', fontWeight: 600 }}>Total amount :</span>
              <span style={{ color: '#059669', fontWeight: 700 }}>₹ {orderTotals.total}</span>
            </div>
          </div>

          {/* Payment Method Selector */}
          <div style={{
            background: '#FFFFFF',
            borderRadius: 8,
            border: '1px solid #E5E7EB',
            padding: '12px 14px',
            marginBottom: 12
          }}>
            <Form.Item
              label={<span style={{ color: '#1F2937', fontWeight: 600, fontSize: 12 }}>Payment method :</span>}
              name="paymentMethod"
              rules={[{ required: true }]}
            >
              <Select style={{ borderRadius: 6, height: 40 }}>
                {['Cash', 'Online', 'Cheque', 'UPI', 'Credit'].map((m) => (
                  <Option key={m} value={m}>{m}</Option>
                ))}
              </Select>
            </Form.Item>
          </div>

          {/* Remarks */}
          <div style={{
            background: '#FFFFFF',
            borderRadius: 8,
            border: '1px solid #E5E7EB',
            padding: '12px 14px',
            marginBottom: 12
          }}>
            <Form.Item
              label={<span style={{ color: '#1F2937', fontWeight: 600, fontSize: 12 }}>Remarks.</span>}
              name="remarks"
            >
              <TextArea
                rows={3}
                placeholder="Type here..."
                style={{ borderRadius: 6 }}
              />
            </Form.Item>
          </div>

          {/* Upload Proof */}
          <div style={{
            background: '#FFFFFF',
            borderRadius: 8,
            border: '1px solid #E5E7EB',
            padding: '12px 14px',
            marginBottom: 16
          }}>
            <Form.Item
              label={<span style={{ color: '#1F2937', fontWeight: 600, fontSize: 12 }}>Upload proof *</span>}
              required
            >
              <Upload
                beforeUpload={(file) => {
                  setOrderProofFile(file);
                  return false;
                }}
                maxCount={1}
                onRemove={() => setOrderProofFile(null)}
              >
                <Button icon={<UploadOutlined />} style={{ borderRadius: 6 }}>
                  Choose File
                </Button>
              </Upload>
            </Form.Item>
          </div>

          {/* Footer Action Buttons */}
          <div style={{ display: 'flex', gap: 12 }}>
            <Button
              onClick={() => setOrderModalVisible(false)}
              style={{
                flex: 1,
                height: 42,
                borderRadius: 8,
                borderColor: '#125EC9',
                color: '#125EC9',
                fontWeight: 600
              }}
            >
              Cancel
            </Button>
            <Button
              type="primary"
              htmlType="submit"
              loading={orderSubmitting}
              style={{
                flex: 1,
                height: 42,
                borderRadius: 8,
                background: '#0059D7',
                borderColor: '#0059D7',
                fontWeight: 600
              }}
            >
              Save Order
            </Button>
          </div>
        </Form>
      </Modal>

      {/* MODAL: ADD CUSTOM MANUAL PRODUCT */}
      <Modal
        title="Add Manual Product"
        open={customProductModalVisible}
        onCancel={() => setCustomProductModalVisible(false)}
        onOk={() => customProductForm.submit()}
        okText="Add Product"
        destroyOnClose
        width={420}
      >
        <Form
          form={customProductForm}
          layout="vertical"
          onFinish={handleAddCustomProduct}
          initialValues={{ size: 'Standard', qty: 1, price: 0 }}
          style={{ marginTop: 12 }}
        >
          <Form.Item
            label="Product Name"
            name="name"
            rules={[{ required: true, message: 'Please enter product name' }]}
          >
            <Input placeholder="Enter product name" style={{ borderRadius: 6 }} />
          </Form.Item>

          <Form.Item label="Size / Variant" name="size">
            <Input placeholder="Enter size (e.g. Standard, 1kg)" style={{ borderRadius: 6 }} />
          </Form.Item>

          <Row gutter={12}>
            <Col span={12}>
              <Form.Item label="Quantity" name="qty" rules={[{ required: true }]}>
                <InputNumber min={1} style={{ width: '100%', borderRadius: 6 }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="Rate (₹)" name="price" rules={[{ required: true }]}>
                <InputNumber min={0} style={{ width: '100%', borderRadius: 6 }} />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>

      {/* MODAL: CLIENT PICKER (Matching SS1 "Pick" button) */}
      <Modal
        title="Pick Client"
        open={clientPickerVisible}
        onCancel={() => setClientPickerVisible(false)}
        footer={null}
        width={520}
        destroyOnClose
      >
        <div style={{ marginBottom: 12 }}>
          <Input
            placeholder="Search clients..."
            prefix={<SearchOutlined />}
            value={clientPickerSearch}
            onChange={(e) => setClientPickerSearch(e.target.value)}
            style={{ borderRadius: 6 }}
          />
        </div>
        <div style={{ maxHeight: 360, overflowY: 'auto' }}>
          {clientsMaster
            .filter((c) =>
              !clientPickerSearch ||
              c.name.toLowerCase().includes(clientPickerSearch.toLowerCase()) ||
              (c.phone && c.phone.includes(clientPickerSearch))
            )
            .map((c) => (
              <div
                key={c.id}
                onClick={() => handleSelectPickedClient(c)}
                style={{
                  padding: '12px 14px',
                  borderRadius: 8,
                  border: '1px solid #E5E7EB',
                  marginBottom: 8,
                  cursor: 'pointer',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  background: '#F9FAFB',
                  transition: 'background 0.2s'
                }}
              >
                <div>
                  <div style={{ fontWeight: 600, color: '#111827' }}>{c.name}</div>
                  <div style={{ fontSize: 12, color: '#6B7280' }}>
                    {c.phone || 'No phone'} • {c.clientType || 'Retailer'} • {c.location || 'No location'}
                  </div>
                </div>
                <Button size="small" type="primary" ghost style={{ borderRadius: 6 }}>
                  Select
                </Button>
              </div>
            ))}
          {clientsMaster.length === 0 && (
            <div style={{ textAlign: 'center', padding: '24px 0', color: '#9CA3AF' }}>
              No clients found in directory.
            </div>
          )}
        </div>
      </Modal>

      {/* 5. MODAL: VISIT DETAILS VIEW */}
      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <EnvironmentOutlined style={{ color: '#059669' }} />
            <span>Party Visit Details</span>
          </div>
        }
        open={visitDetailsVisible}
        onCancel={() => setVisitDetailsVisible(false)}
        footer={[
          <Button key="close" onClick={() => setVisitDetailsVisible(false)} style={{ borderRadius: 6 }}>
            Close
          </Button>
        ]}
        width={560}
      >
        {selectedVisit && (
          <div>
            <div style={{
              background: '#f8fafc',
              padding: '16px',
              borderRadius: 12,
              border: '1px solid #e2e8f0',
              marginBottom: 16
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <Title level={4} style={{ margin: 0 }}>{selectedVisit.clientName || 'Client'}</Title>
                <Tag color={selectedVisit.verified ? 'success' : 'warning'}>
                  {selectedVisit.verified ? 'VERIFIED' : 'PENDING'}
                </Tag>
              </div>

              <div style={{ fontSize: 13, color: '#475569', lineHeight: 1.8 }}>
                <div><strong>Phone:</strong> {selectedVisit.phone || '--'}</div>
                <div><strong>Visit Type:</strong> {selectedVisit.visitType || 'Follow-up'}</div>
                <div><strong>Party Category:</strong> {selectedVisit.clientType || 'Retailer'}</div>
                <div><strong>Logged On:</strong> {dayjs(selectedVisit.visitDate).format('DD MMM YYYY, hh:mm A')}</div>
                <div><strong>Sales Person:</strong> {selectedVisit.salesPerson || 'Ramesh Das'}</div>
                <div><strong>Location:</strong> {selectedVisit.location || selectedVisit.checkInAddress || '--'}</div>
                {selectedVisit.clientOtp && (
                  <div><strong>Verified OTP:</strong> <Tag color="blue">{selectedVisit.clientOtp}</Tag></div>
                )}
              </div>

              {selectedVisit.checkInLat && selectedVisit.checkInLng && (
                <div style={{ marginTop: 12 }}>
                  <a
                    href={`https://www.google.com/maps?q=${selectedVisit.checkInLat},${selectedVisit.checkInLng}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <Button type="primary" ghost size="small" icon={<GlobalOutlined />}>
                      Open Google Maps Location
                    </Button>
                  </a>
                </div>
              )}
            </div>

            {selectedVisit.clientSignatureUrl && (
              <div>
                <Title level={5} style={{ margin: '0 0 8px 0' }}>Attached Proof / Signature</Title>
                <Image
                  src={
                    selectedVisit.clientSignatureUrl.startsWith('http')
                      ? selectedVisit.clientSignatureUrl
                      : `${API_BASE_URL}${selectedVisit.clientSignatureUrl}`
                  }
                  alt="Client Signature"
                  style={{ maxWidth: 260, borderRadius: 8, border: '1px solid #cbd5e1' }}
                />
              </div>
            )}
          </div>
        )}
      </Modal>

      {/* 6. MODAL: ORDER DETAILS VIEW */}
      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <ShoppingCartOutlined style={{ color: '#2563eb' }} />
            <span>Order Invoice Breakdown</span>
          </div>
        }
        open={orderDetailsVisible}
        onCancel={() => setOrderDetailsVisible(false)}
        footer={[
          <Button key="close" onClick={() => setOrderDetailsVisible(false)} style={{ borderRadius: 6 }}>
            Close
          </Button>
        ]}
        width={650}
      >
        {selectedOrder && (
          <div>
            <div style={{
              background: '#f8fafc',
              padding: '16px',
              borderRadius: 12,
              border: '1px solid #e2e8f0',
              marginBottom: 16
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <span style={{ fontSize: 16, fontWeight: 700, color: '#2563eb' }}>
                  Order #ORD-{selectedOrder.id}
                </span>
                <Tag color="cyan">{selectedOrder.paymentMethod || 'Cash'}</Tag>
              </div>

              <div style={{ fontSize: 13, color: '#475569', lineHeight: 1.8 }}>
                <div><strong>Customer Phone:</strong> {selectedOrder.phone || '--'}</div>
                <div><strong>Order Date:</strong> {dayjs(selectedOrder.orderDate).format('DD MMM YYYY, hh:mm A')}</div>
                <div><strong>Net Amount:</strong> {fmtCurrency(selectedOrder.netAmount)}</div>
                <div><strong>GST Amount (18%):</strong> {fmtCurrency(selectedOrder.gstAmount)}</div>
                <div><strong>Grand Total:</strong> <strong style={{ color: '#059669', fontSize: 15 }}>{fmtCurrency(selectedOrder.totalAmount)}</strong></div>
                {selectedOrder.remarks && <div><strong>Remarks:</strong> {selectedOrder.remarks}</div>}
              </div>
            </div>

            {/* Itemized Table */}
            <Title level={5} style={{ margin: '0 0 10px 0' }}>Order Products & Items</Title>
            <Table
              dataSource={selectedOrder.items || []}
              rowKey="id"
              pagination={false}
              size="small"
              columns={[
                {
                  title: 'Product',
                  dataIndex: 'name',
                  key: 'name',
                  render: (n) => <Text strong>{n}</Text>
                },
                {
                  title: 'Size',
                  dataIndex: 'size',
                  key: 'size',
                  render: (s) => s || 'Standard'
                },
                {
                  title: 'Qty',
                  dataIndex: 'qty',
                  key: 'qty'
                },
                {
                  title: 'Unit Price',
                  dataIndex: 'price',
                  key: 'price',
                  render: (p) => fmtCurrency(p)
                },
                {
                  title: 'Total',
                  dataIndex: 'amount',
                  key: 'amount',
                  render: (a) => <strong style={{ color: '#059669' }}>{fmtCurrency(a)}</strong>
                }
              ]}
              style={{ marginBottom: 16 }}
            />

            {/* Proof Attachment */}
            {selectedOrder.proofUrl && (
              <div>
                <Title level={5} style={{ margin: '0 0 8px 0' }}>Payment Proof / Attached Receipt</Title>
                <Image
                  src={
                    selectedOrder.proofUrl.startsWith('http')
                      ? selectedOrder.proofUrl
                      : `${API_BASE_URL}${selectedOrder.proofUrl}`
                  }
                  alt="Payment Proof"
                  style={{ maxWidth: 260, borderRadius: 8, border: '1px solid #cbd5e1' }}
                />
              </div>
            )}
          </div>
        )}
      </Modal>

      {/* ========================================================================= */}
      {/* 7. MODAL: ASSIGNED JOB DETAIL (MATCHING APK AssignedJobDetailScreen 100%)  */}
      {/* ========================================================================= */}
      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <ArrowLeftOutlined
              style={{ cursor: 'pointer', fontSize: 16, color: '#454545' }}
              onClick={() => setJobDetailModalVisible(false)}
            />
            <span style={{ fontSize: 18, color: '#454545', fontWeight: 600 }}>Assigned job</span>
          </div>
        }
        open={jobDetailModalVisible}
        onCancel={() => setJobDetailModalVisible(false)}
        footer={null}
        width={620}
        destroyOnClose
      >
        {selectedJob && (
          <div>
            {/* Notice text matching APK line 131 */}
            <div style={{ padding: '8px 4px 14px 4px' }}>
              <div style={{ color: '#454545', fontSize: 12, lineHeight: '20px' }}>
                View and manage tasks assigned to you by your manager. Complete pending tasks on time.
              </div>
            </div>

            {/* Job card matching APK line 138 */}
            <div style={{
              backgroundColor: '#F3F4F6',
              borderRadius: 8,
              padding: '16px 14px',
              border: '1px solid #E5E7EB',
              marginBottom: 14
            }}>
              {/* Header row: client name */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                <div style={{ fontSize: 16, fontWeight: 600, color: '#454545' }}>
                  {selectedJob.client?.name || selectedJob.client || 'Client'}
                </div>
              </div>

              {/* Address + description */}
              <div style={{ marginBottom: 14 }}>
                <div
                  onClick={() => handleNavigateJob(selectedJob)}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 6,
                    color: '#125EC9',
                    fontSize: 12,
                    fontWeight: 500,
                    textDecoration: 'underline',
                    cursor: 'pointer',
                    marginBottom: 6
                  }}
                >
                  <EnvironmentOutlined />
                  <span>{selectedJob.clientAddress || selectedJob.client?.location || selectedJob.address || 'View location on map'}</span>
                </div>
                <div style={{ fontSize: 12, color: '#6B7280', marginTop: 4, lineHeight: '18px' }}>
                  {selectedJob.description || 'Visit the client to discuss pricing, order confirmation, and collect feedback.'}
                </div>
              </div>

              {/* Map Preview section matching APK lines 162-203 */}
              {((selectedJob.clientLat && selectedJob.clientLng) || selectedJob.clientAddress || selectedJob.client?.location) && (
                <div
                  onClick={() => handleNavigateJob(selectedJob)}
                  style={{
                    borderRadius: 10,
                    border: '1px solid #D1D5DB',
                    overflow: 'hidden',
                    marginBottom: 16,
                    cursor: 'pointer',
                    background: '#EFF6FF',
                    padding: '18px 14px',
                    position: 'relative',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minHeight: 120,
                    transition: 'all 0.2s ease'
                  }}
                >
                  <GlobalOutlined style={{ fontSize: 32, color: '#1D4ED8', marginBottom: 6 }} />
                  <div style={{ fontWeight: 600, color: '#1E40AF', fontSize: 13 }}>
                    {selectedJob.clientLat && selectedJob.clientLng
                      ? `Coordinates: ${Number(selectedJob.clientLat).toFixed(4)}, ${Number(selectedJob.clientLng).toFixed(4)}`
                      : (selectedJob.clientAddress || selectedJob.client?.location || 'Client Location')}
                  </div>
                  <div style={{ fontSize: 11, color: '#6B7280', marginTop: 2 }}>
                    Click to navigate route in Google Maps
                  </div>
                  <div style={{
                    position: 'absolute',
                    bottom: 8,
                    right: 8,
                    background: 'rgba(255, 255, 255, 0.95)',
                    padding: '3px 8px',
                    borderRadius: 4,
                    fontSize: 10,
                    color: '#125EC9',
                    fontWeight: 600,
                    border: '1px solid #BFDBFE'
                  }}>
                    🗺️ Open in Google Maps
                  </div>
                </div>
              )}

              {/* Actions row matching APK lines 206-285 */}
              {(() => {
                const s = String(selectedJob.status || '').toLowerCase();
                const isPending = s === 'pending';
                const hasEnded = !!selectedJob.endedAt;
                const isLocked = isPending || hasEnded;

                return (
                  <div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
                      {/* Client visit button */}
                      <button
                        type="button"
                        onClick={() => {
                          if (isLocked) {
                            message.info(isPending ? 'Please start the job first.' : 'Please wait for admin verification.');
                            return;
                          }
                          setJobDetailModalVisible(false);
                          handleOpenVisitModal(selectedJob.client, selectedJob.id);
                        }}
                        style={{
                          backgroundColor: '#BFDAFF',
                          borderColor: '#BFDAFF',
                          borderWidth: 1,
                          borderStyle: 'solid',
                          padding: '6px 12px',
                          borderRadius: 6,
                          opacity: isLocked ? 0.55 : 1,
                          cursor: isLocked ? 'not-allowed' : 'pointer',
                          color: '#125EC9',
                          fontWeight: 500,
                          fontSize: 11,
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 4
                        }}
                      >
                        <ShopOutlined /> Client visit
                      </button>

                      {/* Client order button */}
                      <button
                        type="button"
                        onClick={() => {
                          if (isLocked) {
                            message.info(isPending ? 'Please start the job first.' : 'Please wait for admin verification.');
                            return;
                          }
                          setJobDetailModalVisible(false);
                          handleOpenOrderModal(selectedJob.client, selectedJob.id);
                        }}
                        style={{
                          backgroundColor: '#E0ECFF',
                          borderColor: '#E0ECFF',
                          borderWidth: 1,
                          borderStyle: 'solid',
                          padding: '6px 12px',
                          borderRadius: 6,
                          opacity: isLocked ? 0.55 : 1,
                          cursor: isLocked ? 'not-allowed' : 'pointer',
                          color: '#1D4ED8',
                          fontWeight: 500,
                          fontSize: 11,
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 4
                        }}
                      >
                        <ShoppingCartOutlined /> Client order
                      </button>

                      {/* Navigate button */}
                      <button
                        type="button"
                        onClick={() => handleNavigateJob(selectedJob)}
                        style={{
                          backgroundColor: '#F3F4F6',
                          borderColor: '#E5E7EB',
                          borderWidth: 1,
                          borderStyle: 'solid',
                          padding: '6px 12px',
                          borderRadius: 6,
                          cursor: 'pointer',
                          color: '#374151',
                          fontWeight: 500,
                          fontSize: 11,
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 4
                        }}
                      >
                        <EnvironmentOutlined /> Navigate
                      </button>

                      {/* Start job button */}
                      {isPending && !selectedJob.startedAt && !selectedJob.endedAt && (
                        <button
                          type="button"
                          disabled={jobActionLoading}
                          onClick={() => handleStartJob(selectedJob.id)}
                          style={{
                            backgroundColor: '#FFD4D4',
                            borderColor: '#FFD4D4',
                            borderWidth: 1,
                            borderStyle: 'solid',
                            padding: '6px 12px',
                            borderRadius: 6,
                            cursor: 'pointer',
                            color: '#FF4747',
                            fontWeight: 600,
                            fontSize: 11,
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 4
                          }}
                        >
                          {jobActionLoading ? <SyncOutlined spin /> : <ClockCircleOutlined />} Start job
                        </button>
                      )}

                      {/* Stop job button */}
                      {s === 'inprogress' && !selectedJob.endedAt && (
                        <button
                          type="button"
                          disabled={jobActionLoading}
                          onClick={() => handleStopJob(selectedJob.id)}
                          style={{
                            backgroundColor: '#DCFCE7',
                            borderColor: '#DCFCE7',
                            borderWidth: 1,
                            borderStyle: 'solid',
                            padding: '6px 12px',
                            borderRadius: 6,
                            cursor: 'pointer',
                            color: '#166534',
                            fontWeight: 600,
                            fontSize: 11,
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 4
                          }}
                        >
                          {jobActionLoading ? <SyncOutlined spin /> : <CheckCircleOutlined />} Stop job
                        </button>
                      )}
                    </div>

                    {/* Enable location prompt matching APK line 287 */}
                    {(isPending || s === 'inprogress') && !selectedJob.endedAt && (
                      <div style={{ marginTop: 8 }}>
                        <a
                          onClick={async () => {
                            const geo = await captureGeo();
                            if (geo.lat) message.success(`Location captured: ${geo.lat}, ${geo.lng}`);
                            else message.info('Please allow location permissions in your browser');
                          }}
                          style={{ fontSize: 11, color: '#6B7280', textDecoration: 'underline' }}
                        >
                          Enable location access
                        </a>
                      </div>
                    )}
                  </div>
                );
              })()}

              {/* Assigned on / Due date side by side matching APK lines 299-321 */}
              <div style={{
                display: 'flex',
                marginTop: 20,
                borderTop: '1px solid #71717A',
                borderBottom: '1px solid #71717A',
                padding: '12px 0'
              }}>
                <div style={{ flex: 1, padding: '0 10px', borderRight: '1px solid #71717A' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <CalendarOutlined style={{ fontSize: 13, color: '#1F2937' }} />
                    <span style={{ color: '#1F2937', fontWeight: 500, fontSize: 12 }}>Assigned on</span>
                  </div>
                  <div style={{ marginTop: 6, color: '#6B7280', fontWeight: 500, fontSize: 12 }}>
                    {selectedJob.assignedOn ? dayjs(selectedJob.assignedOn).format('ddd MMM DD YYYY') : '—'}
                  </div>
                </div>
                <div style={{ flex: 1, padding: '0 10px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <ClockCircleOutlined style={{ fontSize: 13, color: '#1F2937' }} />
                    <span style={{ color: '#1F2937', fontWeight: 500, fontSize: 12 }}>Due date</span>
                  </div>
                  <div style={{ marginTop: 6, color: '#6B7280', fontWeight: 500, fontSize: 12 }}>
                    {selectedJob.dueDate ? dayjs(selectedJob.dueDate).format('ddd MMM DD YYYY') : '—'}
                  </div>
                </div>
              </div>

              {/* Status chip matching APK line 324 */}
              <div style={{ marginTop: 14 }}>
                {(() => {
                  const s = String(selectedJob.status || '').toLowerCase();
                  const conf = s === 'complete'
                    ? { bg: '#DCFCE7', border: '#DCFCE7', color: '#166534', label: 'Complete' }
                    : s === 'inprogress'
                    ? { bg: '#E0ECFF', border: '#E0ECFF', color: '#1D4ED8', label: 'In progress' }
                    : { bg: '#FFF0C7', border: '#FFF0C7', color: '#987500', label: 'Pending' };
                  return (
                    <span style={{
                      backgroundColor: conf.bg,
                      borderRadius: 4,
                      padding: '4px 12px',
                      border: `1px solid ${conf.border}`,
                      color: conf.color,
                      fontSize: 11,
                      fontWeight: 500,
                      display: 'inline-block'
                    }}>
                      {conf.label}
                    </span>
                  );
                })()}
              </div>
            </div>
          </div>
        )}
      </Modal>
    </StaffLayout>
  );
};

export default StaffSales;
